/**
 * What just happened, and what the model would have done instead.
 *
 * This is where the game becomes a training tool rather than a game. Every action the
 * hero took was priced by the reference model at the moment it was taken — before the
 * cards came, so it cannot be hindsight — and the difference between what you did and
 * what it rated best is the number the whole leak report is built from.
 *
 * The bots' own reasoning is shown too. Being able to see WHY the seat next to you
 * shoved is not something a real table offers, and it is most of what makes playing
 * against a bot worth doing at all.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Game, HandRecord } from "@engine/game";
import { cardName, suitChar, suitIsRed, positionOf } from "../lib/table-game";
import { useMoney } from "../lib/MoneyContext";

export function HandHistory({ game }: { game: Game }) {
  const { fmt } = useMoney();
  const [open, setOpen] = useState<number | null>(null);
  const recent = [...game.hands].reverse().slice(0, 25);

  if (recent.length === 0) {
    return (
      <section className="panel">
        <h2>Hand history</h2>
        <p className="muted small">Nothing played yet.</p>
        <SessionProof game={game} />
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Hand history</h2>
      <p className="muted small">
        Each of your decisions is priced by our model at the moment you made it — before
        the next card — so nothing here is hindsight. It is a heuristic, not a solve.
      </p>
      <SessionProof game={game} />
      <ol className="hh">
        {recent.map((h) => (
          <li key={h.index}>
            <button className="hh-row" onClick={() => setOpen(open === h.index ? null : h.index)}
                    aria-expanded={open === h.index}>
              <span className="hh-idx num">#{h.index + 1}</span>
              <span className="hh-cards">
                {(h.holes[game.config.heroSeat] ?? []).map((c, i) => <MiniCard key={i} card={c} />)}
              </span>
              <span className="hh-board">
                {h.board.map((c, i) => <MiniCard key={i} card={c} />)}
              </span>
              <span className={`hh-net num ${netTone(h, game.config.heroSeat)}`}>
                {signed(h.result.net[game.config.heroSeat] ?? 0, fmt)}
              </span>
              <span className="hh-lost num">{lostLabel(h, game.config.heroSeat, fmt)}</span>
            </button>
            {open === h.index && <HandDetail game={game} hand={h} />}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The session commitment, and the seed it commits to.
 *
 * The commitment is fixed when the table is created — before any hand — and it is
 * shown from that moment. The seed itself sits behind a click, because revealing it
 * mid-session tells you every hand you have not played yet, which would ruin the
 * session rather than verify it. Once the seed is out, one value replays the whole
 * table on the shuffle page.
 */
function SessionProof({ game }: { game: Game }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="sessionproof">
      <p className="muted small">
        This table was committed to before the first card:{" "}
        <code className="seedcode">{game.sessionCommitment}</code>
      </p>
      {shown ? (
        <p className="muted small">
          Session seed <code className="seedcode">{game.revealSeed()}</code> — paste it
          into <Link to="/fair">the shuffle page</Link> to replay any hand of this table.
        </p>
      ) : (
        <button className="btn-quiet btn-tiny" onClick={() => setShown(true)}>
          Reveal the session seed
        </button>
      )}
      {shown && (
        <p className="muted small">
          Now that it is out, every hand still to come is knowable from it. That is what
          makes the commitment meaningful — and why it is worth finishing the session
          before you look.
        </p>
      )}
    </div>
  );
}

function HandDetail({ game, hand }: { game: Game; hand: HandRecord }) {
  const { fmt } = useMoney();
  const n = game.config.seats;
  let street = "";
  return (
    <div className="hh-detail">
      {hand.actions.map((a, i) => {
        const head = a.street !== street ? (street = a.street) : null;
        const isHero = a.seat === game.config.heroSeat;
        return (
          <div key={i}>
            {head && <p className="hh-street">{head}</p>}
            <p className={`hh-act${isHero ? " hh-act-hero" : ""}`}>
              <span className="hh-who">
                {game.seats[a.seat]!.name}
                <em> {positionOf(a.seat, hand.button, n)}</em>
              </span>
              <span className="hh-what">
                {a.decision?.label ?? describe(a.action.kind, a.amount, fmt)}
              </span>
            </p>
            {a.decision?.why && <p className="hh-why muted">{a.decision.why}</p>}
            {isHero && a.graded && a.graded.evLost > 0.01 && (
              <p className="hh-cost">
                our model preferred <strong>{a.graded.bestLabel}</strong>, worth{" "}
                {fmt(a.graded.evLost)} more
              </p>
            )}
          </div>
        );
      })}
      <p className="hh-summary muted small">
        Board {hand.board.map((c) => label(c)).join(" ") || "—"} ·{" "}
        {hand.result.showdown ? "showdown" : "no showdown"}
      </p>
      {/*
        The seed and the commitment, so the deal can be checked rather than trusted.
        The commitment was recorded before a card came off the deck; the seed is only
        shown now that the hand is over. Both are here rather than behind a link
        because a verification affordance nobody can find is decoration.
      */}
      <details className="hh-fair">
        <summary className="muted small">Verify this deal</summary>
        <dl className="fairpair">
          <dt>Committed before the deal</dt>
          <dd><code className="seedcode">{hand.commitment}</code></dd>
          <dt>Seed, revealed after</dt>
          <dd><code className="seedcode">{hand.seed}</code></dd>
        </dl>
        <p className="muted small">
          SHA-256 of the seed is the commitment. Check it on{" "}
          <Link to="/fair">the shuffle page</Link>, or in a terminal:
        </p>
        <pre className="fairshell">echo -n {hand.seed} | xxd -r -p | sha256sum</pre>
      </details>
    </div>
  );
}

function MiniCard({ card }: { card: number }) {
  const { rank, suit } = cardName(card);
  return (
    <span className={`mc${suitIsRed(suit) ? " mc-red" : ""}`}>
      {rank}{suitChar(suit)}
    </span>
  );
}

const label = (c: number) => `${cardName(c).rank}${suitChar(cardName(c).suit)}`;

/**
 * These take the formatter rather than reaching for one.
 *
 * They are plain functions outside any component, so they cannot call `useMoney`.
 * Passing it in keeps them pure and testable, and — more to the point — means the
 * history cannot end up printing chips while the table beside it prints big blinds.
 */
type Fmt = (v: number) => string;

function describe(kind: string, amount: number, fmt: Fmt): string {
  if (kind === "fold") return "folds";
  if (kind === "check") return "checks";
  if (kind === "call") return `calls ${fmt(amount)}`;
  if (kind === "allin") return "all in";
  return `${kind} ${fmt(amount)}`;
}

function signed(v: number, fmt: Fmt): string {
  return v > 0 ? `+${fmt(v)}` : fmt(v);
}

function netTone(h: HandRecord, seat: number): string {
  const v = h.result.net[seat] ?? 0;
  return v > 0 ? "hh-up" : v < 0 ? "hh-down" : "";
}

function lostLabel(h: HandRecord, seat: number, fmt: Fmt): string {
  let lost = 0;
  for (const a of h.actions) if (a.seat === seat && a.graded) lost += a.graded.evLost;
  return lost > 0.01 ? `−${fmt(lost)}` : "";
}
